// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import type { NodeFeed } from './conductors/flowPulse';
import type { StatusToken } from '../../../utils/entityHelpers';
import type { PowerFlowNode } from '../../../utils/powerFlowLayout';
import type { AssetBadge } from '../../../utils/powerFlowCaptions';
import { DownstreamMark, type DownstreamStatus } from './primitives/DownstreamMark';
import { StatusMark } from './primitives/StatusMark';
import { withAlarmLines } from '../../../utils/powerFlowAlarms';
import { glyphFor, type RunState } from './symbols';
import { SYMBOL_NODE_SIZE, isSymbolNode } from '../../../config/powerFlowSymbols';

export interface SldNodeProps {
  node: PowerFlowNode;
  status: StatusToken;
  /** Whether power is reaching it: the status dot pulses with the flow, or dims. */
  feed?: NodeFeed;
  /** Short label — the asset's published name, or a derived code. */
  label: string;
  /** Full label plus state, figures and depth. */
  accessibleName: string;
  /** The one state word: `Standby`, `Supplying load`, `Running`. */
  state?: string;
  /** True when the state is inferred. Drawn italic, never by colour alone. */
  stateInferred?: boolean;
  /** Why the state was inferred. Reaches the hover title only. */
  stateRule?: string;
  /** One line per open alarm on this asset. Reaches the hover title only. */
  alarmLines?: string[];
  /** At most two figures, in drawing order. */
  values?: string[];
  /** True when some of this asset's readings are stale; the figures are greyed. */
  stale?: boolean;
  /** Rotating machines: fills the glyph when running, dashes it when unknown. */
  run?: RunState;
  badge?: AssetBadge;
  /**
   * The worst status among loads folded into this box and not drawn — the
   * racks behind an RPP's `14 IT Racks`. Drawn as a second mark so the fold
   * cannot hide a fault; the accessible name spells the counts out.
   */
  downstream?: DownstreamStatus;
  selected: boolean;
  /** True when the selected asset feeds this one, or is fed by it. */
  highlighted: boolean;
  /** Exactly one asset per rank is focusable at a time (roving tabIndex). */
  focusable: boolean;
  onSelect: (_id: string) => void;
}

/**
 * One device on the diagram: a box with a name, a state and up to two figures.
 *
 * **This is a schematic device, not a card.** A one-line's device box carries
 * the least text that identifies it — a name, a state word, a figure or two —
 * and no equipment-type line: what a box *is* comes from where it sits in the
 * chain and which block column holds it, exactly as on the site's own Ignition
 * sheets. Those sheets do draw a glyph beside the text — a transformer's
 * coils, a generator's `G`, a UPS's wave and battery — so the glyph sits to the
 * right, decorative and `aria-hidden`, with the type spoken in the accessible
 * name instead.
 *
 * Status is expressed sparingly and that restraint is deliberate: healthy and
 * no-data boxes stay neutral, and only degraded and fault carry colour. It is
 * what keeps a 600-asset drawing readable. `StatusMark` also varies the mark's
 * *shape*, because colour alone would fail WCAG 1.4.1 for exactly the two
 * states an operator cannot afford to miss.
 *
 * An inferred state is italic rather than a different colour, which is the same
 * channel the reference uses and the only one left once red means energized
 * (ADR-0016).
 */
const SldNodeComponent = ({
  node,
  status,
  feed,
  label,
  accessibleName,
  state,
  stateInferred,
  stateRule,
  alarmLines,
  values,
  stale,
  run,
  badge,
  downstream,
  selected,
  highlighted,
  focusable,
  onSelect,
}: SldNodeProps) => {
  const Glyph = glyphFor(node.vertex.symbol);
  // A device with a one-line symbol *is* its symbol: drawn at the node's left,
  // on the conductor, with the text beside it and no box. A symbol nobody has
  // drawn keeps the box.
  const asSymbol = isSymbolNode(node.vertex.symbol);
  // A chain ends at its icon: no lead up from a device nothing feeds, none down
  // from one that feeds nothing drawn.
  const leads = { top: node.vertex.in.length > 0, bottom: node.vertex.out.length > 0 };
  const classes = [
    'sld-node',
    asSymbol ? 'sld-node--symbol' : 'sld-node--box',
    `sld-node--${status}`,
    `sld-node--role-${node.vertex.role}`,
  ];
  if (selected) classes.push('is-selected');
  if (highlighted) classes.push('is-highlighted');

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      onSelect(node.id);
    }
  };

  return (
    <div
      className={classes.join(' ')}
      style={{ left: node.x, top: node.y, width: node.w, height: node.h }}
      role="gridcell"
      tabIndex={focusable ? 0 : -1}
      aria-label={accessibleName}
      aria-current={selected ? 'true' : undefined}
      title={withAlarmLines(stateRule ? `${state}: ${stateRule}` : undefined, alarmLines)}
      data-sld-node={node.id}
      data-sld-status={status}
      data-sld-symbol={node.vertex.symbol}
      data-sld-rank={node.rank}
      onClick={() => onSelect(node.id)}
      onKeyDown={handleKeyDown}
    >
      <span className="sld-node__status">
        <StatusMark status={status} feed={feed} />
        <DownstreamMark downstream={downstream} className="sld-node__downstream" />
      </span>
      {asSymbol ? (
        <span
          className={`sld-node__symbol${leads.top ? '' : ' sld-node__symbol--no-top'}${
            leads.bottom ? '' : ' sld-node__symbol--no-bottom'
          }`}
          aria-hidden="true"
        >
          <Glyph size={SYMBOL_NODE_SIZE} run={run} leads={leads} />
        </span>
      ) : (
        <span className="sld-node__glyph" aria-hidden="true">
          <Glyph size={18} run={run} />
        </span>
      )}
      <span className="sld-node__text">
        <span className="sld-node__label">{label}</span>
        {state && (
          <span
            className={`sld-node__state${stateInferred ? ' is-inferred' : ''}`}
            data-sld-state={state}
            data-sld-inferred={stateInferred ? 'true' : undefined}
          >
            {state}
          </span>
        )}
        {(values ?? []).map(value => (
          <span key={value} className={`sld-node__value${stale ? ' is-stale' : ''}`} data-sld-value>
            {value}
          </span>
        ))}
      </span>
      {badge && (
        <span
          className={`sld-badge sld-badge--${badge.tone}`}
          data-sld-badge={badge.tone}
          title={badge.rule}
        >
          {badge.text}
        </span>
      )}
    </div>
  );
};

/**
 * Memoised so a telemetry poll only re-renders the boxes whose state actually
 * changed. Geometry is stable across polls, so most boxes bail out here.
 */
export const SldNode = React.memo(SldNodeComponent);
